import { tokenValues } from '@client/design-system/__generated__';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { trpc } from '@client/trpc/client';
import { cn } from '@client/utils';
import { SYSTEM_ACCOUNT_IDS } from '@shared/constants';
import { skipToken } from '@tanstack/react-query';
import { useInViewport } from 'ahooks';
import { Avatar, Tag, Typography } from 'antd';
import clsx from 'clsx';
import { CheckCircle, IdCard, Users, XCircle } from 'lucide-react';
import { useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { normalizeAvatarSrc } from './avatar-src';
import { shouldLoadInlineUserData, shouldLoadUserDetails } from './display-query-guards';
import { EntityPopover } from './EntityPopover';
import { SystemActor } from './SystemActor';

type Props = {
    photoUrl?: string | null;
    name?: string | null;
    size?: 'x-small' | 'small' | 'medium' | 'large';
    className?: string;
    classNames?: {
        avatar?: string;
        name?: string;
    };
    userId?: bigint | null;
    orgId?: bigint;
    showPopover?: boolean;
    unallocatedLabel?: string;
    showAvatar?: boolean;
    showName?: boolean;
    inline?: boolean;
    inViewPort?: boolean;
    disabled?: boolean;
    showStatus?: 'always' | 'disabled-only' | 'never';
    /** Caller supplied authoritative inline name/avatar data, including null values. */
    skipInlineLookup?: boolean;
};

const SIZES = {
    'x-small': 14,
    small: 16,
    medium: 22,
    large: 28,
} as const;

// Sized so the initials sit inside the circle like the generated (Auth0/Gravatar) avatar images,
// instead of antd's default text size, which fills the whole avatar with one giant letter.
const INITIALS_FONT_SIZES = tokenValues.typography.avatarInitials;

/** "Alex Simpson" → "AS" — initials fallback when the user has no photo. */
export const avatarInitials = (name: string | null | undefined): string | undefined =>
    name
        ?.trim()
        .split(/\s+/)
        .map((word) => word.charAt(0).toUpperCase())
        .slice(0, 2)
        .join('') || undefined;

const unallocatedPhotoUrl =
    'https://s.gravatar.com/avatar/514614ce39a3e0ef7f882c91e6a4981d?s=128&r=pg&d=https%3A%2F%2Fcdn.auth0.com%2Favatars%2Fu.png';

export function User(props: Omit<Props, 'inViewPort'>) {
    const ref = useRef<HTMLDivElement>(null);
    const [inViewPort] = useInViewport(ref);

    return (
        <div ref={ref}>
            <UserInner {...props} inViewPort={inViewPort ?? false} />
        </div>
    );
}

const UserInner = ({
    name: nameProp,
    photoUrl: photoUrlProp,
    orgId: orgIdProp,
    userId,
    size = 'medium',
    className,
    classNames,
    showPopover = true,
    showAvatar = true,
    showName = true,
    unallocatedLabel = 'Unallocated',
    inline = false,
    inViewPort = false,
    disabled = false,
    showStatus = 'disabled-only',
    skipInlineLookup = false,
}: Props) => {
    const currentOrgId = useOrgId();
    const [isPopoverRequested, setIsPopoverRequested] = useState(false);

    const shouldLoadInlineData =
        !skipInlineLookup &&
        shouldLoadInlineUserData({
            name: nameProp,
            photoUrl: photoUrlProp,
            showAvatar,
        });
    // The platform-system account is rendered as a distinct "System" actor, not
    // a person — skip the user lookup and short-circuit the render below. Only
    // the explicit SYSTEM sentinel qualifies; a null userId still means
    // "Unallocated" here (different semantics from audit-log rows).
    const isSystemActor = userId != null && BigInt(userId) === SYSTEM_ACCOUNT_IDS.SYSTEM;

    const orgId = BigInt(orgIdProp || currentOrgId);
    const shouldQueryUser = shouldLoadUserDetails({
        userId,
        inViewPort,
        shouldLoadInlineData,
        shouldLoadPopoverData: showPopover && isPopoverRequested,
    });
    const queryUserId = !isSystemActor && shouldQueryUser && userId ? BigInt(userId) : null;

    const { data: user, isPending } = trpc.user.getUserForDisplay.useQuery(
        queryUserId
            ? {
                  id: queryUserId,
                  orgId,
                  ...(showPopover && isPopoverRequested && { include: ['HandlingParties'] }),
              }
            : skipToken,
    );

    if (isSystemActor) {
        return (
            <SystemActor
                size={size}
                inline={inline}
                showAvatar={showAvatar}
                showName={showName}
                className={className}
                classNames={classNames}
            />
        );
    }

    const name = nameProp || user?.fullName || user?.email;
    const photoUrl = normalizeAvatarSrc(photoUrlProp || user?.photoUrl);
    const shouldShowStatus = showStatus === 'always' || (showStatus === 'disabled-only' && disabled);

    if (!userId && !name) {
        return (
            <div className={clsx('flex items-center', SIZES[size] < SIZES.large ? 'gap-1' : 'gap-2', className)}>
                {showAvatar && (
                    <Avatar
                        size={SIZES[size]}
                        style={{ minWidth: SIZES[size] }}
                        src={unallocatedPhotoUrl}
                        className={classNames?.avatar}
                    />
                )}
                {showName && <div className={classNames?.name}>{unallocatedLabel}</div>}
            </div>
        );
    }

    if (showPopover && userId) {
        return (
            <div className={clsx('flex items-center', SIZES[size] < SIZES.large ? 'gap-1' : 'gap-2', className)}>
                <EntityPopover
                    canBeOpened={!!user}
                    isLoading={isPending}
                    onOpenIntentChange={setIsPopoverRequested}
                    avatar={{
                        src: photoUrl,
                        children: !photoUrl && avatarInitials(name),
                    }}
                    title={name || 'User'}
                    subtitle="User"
                    items={[
                        { type: 'email', value: user?.email },
                        { type: 'phone', value: user?.phone },
                        {
                            icon: ({ className }) => <IdCard className={cn(className, 'text-purple-500')} />,
                            label: 'ID',
                            value: (
                                <span className="rounded bg-muted px-2 py-0.5 font-mono text-xs">
                                    {String(user?.userId)}
                                </span>
                            ),
                            ...(user?.userId != null && {
                                copyableProps: {
                                    value: String(user.userId),
                                },
                            }),
                        },
                        {
                            icon: ({ className }) =>
                                user?.disabled ? (
                                    <XCircle className={cn(className, 'text-red-500')} />
                                ) : (
                                    <CheckCircle className={cn(className, 'text-green-500')} />
                                ),
                            label: 'Status',
                            value: user?.disabled ? (
                                <Tag variant="outlined" color="error">
                                    Disabled
                                </Tag>
                            ) : (
                                <Tag variant="outlined" color="success">
                                    Active
                                </Tag>
                            ),
                        },
                        {
                            layout: 'vertical',
                            icon: ({ className }) => <Users className={cn(className, 'text-cyan-500')} />,
                            label: 'Handling parties',
                            value: user?.HandlingParties?.length ? (
                                <div className="flex flex-wrap gap-1">
                                    {user.HandlingParties.map(({ id, name }) => (
                                        <Tag variant="outlined" key={id} color="blue" className="m-0">
                                            {name}
                                        </Tag>
                                    ))}
                                </div>
                            ) : (
                                <Typography.Text type="secondary">None</Typography.Text>
                            ),
                        },
                    ]}
                >
                    <span
                        className={clsx(
                            'items-center',
                            inline ? 'inline-flex' : 'flex',
                            SIZES[size] < SIZES.large ? 'gap-1' : 'gap-2',
                        )}
                    >
                        {showAvatar && (
                            <Avatar
                                size={SIZES[size]}
                                // gap=2 stops antd's auto-scale shrinking the initials below the configured size
                                gap={2}
                                style={{ minWidth: SIZES[size], fontSize: INITIALS_FONT_SIZES[size] }}
                                src={photoUrl}
                                className={classNames?.avatar}
                            >
                                {!photoUrl && avatarInitials(name)}
                            </Avatar>
                        )}
                        {showName && (
                            <div className={twMerge(size === 'large' && 'font-medium text-base', classNames?.name)}>
                                {name}
                            </div>
                        )}
                    </span>
                </EntityPopover>
                {shouldShowStatus && (
                    <Tag variant="outlined" color={disabled ? 'error' : 'success'}>
                        {disabled ? 'Disabled' : 'Active'}
                    </Tag>
                )}
            </div>
        );
    }

    return (
        <div
            className={clsx(
                'flex items-center',
                inline ? 'inline-flex' : 'flex',
                SIZES[size] < SIZES.large ? 'gap-1' : 'gap-2',
                className,
            )}
        >
            {showAvatar && (
                <Avatar
                    size={SIZES[size]}
                    // gap=2 stops antd's auto-scale shrinking the initials below the configured size
                    gap={2}
                    style={{ minWidth: SIZES[size], fontSize: INITIALS_FONT_SIZES[size] }}
                    src={photoUrl}
                    className={classNames?.avatar}
                >
                    {!photoUrl && avatarInitials(name)}
                </Avatar>
            )}
            {showName && (
                <div className={twMerge(size === 'large' && 'font-medium text-base', classNames?.name)}>{name}</div>
            )}
            {shouldShowStatus && (
                <Tag variant="outlined" color={disabled ? 'error' : 'success'}>
                    {disabled ? 'Disabled' : 'Active'}
                </Tag>
            )}
        </div>
    );
};
